'use client';
import React from 'react';

import styled from '@benzinga/themetron';
import { ErrorBoundary } from '@benzinga/core-ui';
import Hooks from '@benzinga/hooks';

import { isMobile } from '@benzinga/device-utils';
import { NavigationHeaderProps } from '../../../../entities';
import { MenuInterface } from '../../interfaces';
import { NavigationLogo } from '../../NavigationLogo';
import { Menu } from '../../Menu';
import { PrimaryMenuWrapper } from '../../HeaderDesktop';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { SimpleSearch } from './SimpleSearch';
import { SimpleHeaderBar } from './SimpleHeaderBar';

export interface NavigationMenus {
  primary: MenuInterface[];
  secondary: MenuInterface[];
}

export const SimpleHeaderDesktop = ({ hideMenuBar, logoVariant, menus, sticky }: NavigationHeaderProps) => {
  const [isNavBarFixed, setIsNavBarFixed] = React.useState(false);

  const desktopMenuWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const { height } = Hooks.useRefElementSize(desktopMenuWrapperRef);

  Hooks.useEventListener('scroll', () => {
    if (!sticky || isMobile()) return;
    const navbarHeight = height || 132;
    if (window.scrollY >= navbarHeight && !isNavBarFixed) {
      setIsNavBarFixed(true);
    } else if (window.scrollY < navbarHeight && isNavBarFixed) {
      setIsNavBarFixed(false);
    }
  });
  const { i18n } = useTranslation(); // Access i18n from useTranslation
  const currentLocale = i18n.language; // Get the current locale
  const todayDate = DateTime.now().setLocale(currentLocale).toFormat('EEEE, MMMM dd');

  const redirectToSearch = (value: string) => {
    window.location.replace(`https://${window.location.host}/?s=${value}`);
  };

  return (
    <>
      {isNavBarFixed ? (
        <div
          className="navbar-placeholder"
          style={{ height: `${desktopMenuWrapperRef.current?.offsetHeight || 0}px` }}
        ></div>
      ) : null}
      <DesktopMenuWrapper $isFixed={isNavBarFixed} id="navigation-header" ref={desktopMenuWrapperRef}>
        <SimpleHeaderBar />
        <MainContainer className="flex flex-nowrap justify-center justify-items-center py-7 border-b">
          <ErrorBoundary name="benzinga-logo-wrapper">
            <NavigationLogo deviceType="desktop" logoVariant={logoVariant} />
          </ErrorBoundary>
        </MainContainer>
        <SimpleMenuContainer>
          <div className="text-xs flex items-center">{todayDate}</div>
          {!hideMenuBar && (
            <PrimaryMenuWrapper>
              <MenuItemContainer>
                {menus?.primary?.map((menu: MenuInterface, i: number) => {
                  return (
                    <li key={i}>
                      <Menu menu={menu} />
                    </li>
                  );
                })}
              </MenuItemContainer>
            </PrimaryMenuWrapper>
          )}
          <div className="flex justify-end items-center">
            <SimpleSearch onSearch={redirectToSearch} />
          </div>
        </SimpleMenuContainer>
      </DesktopMenuWrapper>
    </>
  );
};

const SimpleMenuContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-columns: minmax(max-content, 1fr) auto minmax(max-content, 1fr);
  padding: 0 30px;
  border-bottom: 2px solid #e5e7eb;
  height: 51px;
`;

const MainContainer = styled.div`
  height: 96px;
`;

const MenuItemContainer = styled.ul<{ isIndiaApp?: boolean }>`
  display: flex;
  overflow-x: scroll;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  justify-content: center;

  li {
    flex-shrink: 0;

    .menu-items {
      padding: 0;
    }

    .menu-item-link {
      font-family: inherit;
      font-weight: 600;
      color: #161616;

      &:hover {
        background: none;
        color: #dd0017;

        .menu-item-link-chevron {
          color: #dd0017;
        }
      }
    }

    .sub-group-wrapper {
      padding: 0;
      border: 1px solid rgba(0, 0, 0, 0.07);
      box-shadow:
        rgba(0, 0, 0, 0.08) 0px 2px 3px -1px,
        rgba(0, 0, 0, 0.06) 0px 4px 11px -2px;
    }

    .dropdown-menu-item {
      border-bottom: 1px solid rgba(0, 0, 0, 0.07);
      min-width: 235px;
      max-width: 350px;

      a {
        font-size: 13px;
        padding: 9px 22px;
        font-weight: 400;
        font-family: inherit;

        &:hover {
          color: #dd0017;
        }
      }

      &:last-child {
        border-bottom: 0;
      }
    }
  }
`;

const DesktopMenuWrapper = styled.div<{ $isFixed?: boolean }>`
  display: none;
  position: ${({ $isFixed }) => ($isFixed ? 'fixed' : 'unset')};
  top: ${({ $isFixed }) => ($isFixed ? '0' : 'unset')};
  width: ${({ $isFixed }) => ($isFixed ? '100%' : 'unset')};
  //z-index: ${({ $isFixed }) => ($isFixed ? '100' : 'unset')};
  z-index: 999999;
  box-shadow: ${({ $isFixed, theme }) => ($isFixed ? theme.shadow.lg : 'unset')};
  background-color: #fff;
  transition: box-shadow 0.3s ease-in-out;
  animation: ${({ $isFixed }) => ($isFixed ? 'slideDown 0.3s ease-in-out' : 'none')};

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .logo-wrapper {
    width: auto;
  }

  @media screen and (min-width: 800px) {
    display: block;
  }
`;
